<template>
  <div class="relatorio-inadimplencia">
    <div class="d-flex align-items-center justify-content-between mb-5">
      <div>
        <h2 class="font-weight-bolder mb-1">Relatório de Inadimplência</h2>
        <p class="text-muted mb-0">
          Compare clientes finais e parceiros por saldo vencido.
        </p>
      </div>
    </div>

    <div class="card card-custom gutter-b">
      <div class="card-body">
        <b-form @submit.prevent="gerarRelatorio">
          <b-row>
            <b-col md="3">
              <b-form-group label="Tipo" label-for="inadimplencia-tipo">
                <b-form-select
                  id="inadimplencia-tipo"
                  v-model="filtros.tipo"
                  :options="tipos"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Data inicial" label-for="inadimplencia-inicio">
                <b-form-input
                  id="inadimplencia-inicio"
                  v-model="filtros.data_inicial"
                  type="date"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Data final" label-for="inadimplencia-fim">
                <b-form-input
                  id="inadimplencia-fim"
                  v-model="filtros.data_final"
                  type="date"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Faixa de atraso" label-for="inadimplencia-faixa">
                <b-form-select
                  id="inadimplencia-faixa"
                  v-model="filtros.faixa_atraso"
                  :options="faixas"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Cliente ou parceiro">
                <VueSelect
                  v-model="pessoaSelecionada"
                  :options="pessoas"
                  :filterable="false"
                  label="nome"
                  placeholder="Digite ao menos 3 caracteres"
                  @input="selecionarPessoa"
                  @search="buscarPessoa"
                >
                  <template slot="no-options">
                    Digite o nome ou documento da pessoa
                  </template>
                  <template slot="option" slot-scope="option">
                    <strong>{{ option.nome }}</strong>
                    <small v-if="option.cnpj_cpf" class="d-block text-muted">
                      {{ option.cnpj_cpf }}
                    </small>
                  </template>
                </VueSelect>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                label="Valor mínimo em atraso"
                label-for="inadimplencia-minimo"
              >
                <b-form-input
                  id="inadimplencia-minimo"
                  v-model.number="filtros.valor_minimo"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0,00"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex flex-wrap justify-content-end mt-2">
            <b-button
              size="sm"
              variant="primary"
              type="submit"
              class="ml-2 mb-2 px-5"
              :disabled="loading || !!exportando"
            >
              <b-spinner v-if="loading" small class="mr-2" />
              Gerar relatório
            </b-button>
            <b-button
              size="sm"
              variant="outline-success"
              type="button"
              class="ml-2 mb-2 px-5"
              :disabled="loading || !!exportando"
              @click="exportarXlsx"
            >
              <b-spinner v-if="exportando === 'xlsx'" small class="mr-2" />
              Exportar XLSX
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              type="button"
              class="ml-2 mb-2 px-5"
              :disabled="loading || !!exportando"
              @click="abrirPdf"
            >
              <b-spinner v-if="exportando === 'pdf'" small class="mr-2" />
              Imprimir PDF
            </b-button>
          </div>
        </b-form>
      </div>
    </div>

    <b-alert :show="!!erro" variant="danger" class="mb-5">
      {{ erro }}
    </b-alert>

    <div v-if="loading && !dados" class="text-center py-15">
      <b-spinner variant="primary" />
      <p class="text-muted mt-3">Calculando a inadimplência...</p>
    </div>

    <template v-else-if="dados">
      <b-row>
        <b-col lg="4" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-body">
              <div class="text-uppercase font-weight-bolder text-muted mb-3">
                Inadimplência total
              </div>
              <div class="display-4 font-weight-bolder text-primary mb-3">
                {{ moeda(dados.resumo.valor_total) }}
              </div>
              <div class="text-dark-50 line-height-lg">
                {{ inteiro(dados.resumo.quantidade_titulos) }} títulos vencidos<br />
                {{ inteiro(dados.resumo.quantidade_inadimplentes) }} inadimplentes
              </div>
            </div>
          </div>
        </b-col>
        <b-col lg="4" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-body">
              <div class="text-uppercase font-weight-bolder text-muted mb-3">
                Cliente final
              </div>
              <div class="display-4 font-weight-bolder text-info mb-3">
                {{ moeda(dados.cliente_final.valor_vencido) }}
              </div>
              <div class="text-dark-50 line-height-lg">
                <strong>{{ percentual(dados.cliente_final.percentual_total) }}</strong>
                da inadimplência total<br />
                Taxa de inadimplência:
                {{ percentual(dados.cliente_final.taxa_inadimplencia) }}<br />
                {{ inteiro(dados.cliente_final.quantidade_inadimplentes) }} clientes
              </div>
            </div>
          </div>
        </b-col>
        <b-col lg="4" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-body">
              <div class="text-uppercase font-weight-bolder text-muted mb-3">
                Parceiros
              </div>
              <div class="display-4 font-weight-bolder text-warning mb-3">
                {{ moeda(dados.parceiros.valor_vencido) }}
              </div>
              <div class="text-dark-50 line-height-lg">
                <strong>{{ percentual(dados.parceiros.percentual_total) }}</strong>
                da inadimplência total<br />
                Taxa de inadimplência:
                {{ percentual(dados.parceiros.taxa_inadimplencia) }}<br />
                {{ inteiro(dados.parceiros.quantidade_inadimplentes) }} parceiros
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-alert
        v-if="!temDados"
        show
        variant="light"
        class="text-center border py-8"
      >
        Nenhuma inadimplência foi encontrada para os filtros informados.
      </b-alert>

      <template v-else>
        <b-row>
          <b-col lg="5" class="mb-5">
            <div class="card card-custom h-100">
              <div class="card-header border-0">
                <div class="card-title">
                  <h3 class="card-label">Composição da inadimplência</h3>
                </div>
              </div>
              <div class="card-body pt-0">
                <div class="mb-7">
                  <div class="d-flex justify-content-between mb-2">
                    <strong>Clientes finais</strong>
                    <strong>{{ percentual(dados.cliente_final.percentual_total) }}</strong>
                  </div>
                  <b-progress :max="100" height="14px">
                    <b-progress-bar
                      :value="percentualSeguro(dados.cliente_final.percentual_total)"
                      variant="info"
                    />
                  </b-progress>
                </div>
                <div class="mb-7">
                  <div class="d-flex justify-content-between mb-2">
                    <strong>Parceiros</strong>
                    <strong>{{ percentual(dados.parceiros.percentual_total) }}</strong>
                  </div>
                  <b-progress :max="100" height="14px">
                    <b-progress-bar
                      :value="percentualSeguro(dados.parceiros.percentual_total)"
                      variant="warning"
                    />
                  </b-progress>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg="7" class="mb-5">
            <div class="card card-custom h-100">
              <div class="card-header border-0">
                <div class="card-title">
                  <h3 class="card-label">Inadimplência por tempo de atraso</h3>
                </div>
              </div>
              <div class="card-body pt-0">
                <b-table small responsive hover :items="dados.aging" :fields="camposAging">
                  <template #cell(cliente_final)="row">{{ moeda(row.value) }}</template>
                  <template #cell(parceiros)="row">{{ moeda(row.value) }}</template>
                  <template #cell(total)="row">
                    <strong>{{ moeda(row.value) }}</strong>
                  </template>
                </b-table>
              </div>
            </div>
          </b-col>
        </b-row>

        <div class="card card-custom gutter-b">
          <div class="card-header border-0">
            <div class="card-title">
              <h3 class="card-label">Inadimplentes</h3>
            </div>
          </div>
          <div class="card-body pt-0">
            <b-table
              responsive
              hover
              show-empty
              empty-text="Nenhum inadimplente encontrado."
              :items="inadimplentes"
              :fields="camposInadimplentes"
            >
              <template #cell(tipo)="row">
                <b-badge :variant="row.value === 'CLIENTE_FINAL' ? 'info' : 'warning'">
                  {{ rotuloTipo(row.value) }}
                </b-badge>
              </template>
              <template #cell(vendedores)="row">
                {{ (row.value || []).join(", ") || "-" }}
              </template>
              <template #cell(vencimento_mais_antigo)="row">
                {{ data(row.value) }}
              </template>
              <template #cell(dias_maior_atraso)="row">
                {{ inteiro(row.value) }} dias
              </template>
              <template #cell(valor_vencido)="row">
                <strong>{{ moeda(row.value) }}</strong>
              </template>
              <template #cell(percentual_sobre_total)="row">
                {{ percentual(row.value) }}
              </template>
              <template #cell(acoes)="row">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  :disabled="row.item.carregandoTitulos || !row.item.id"
                  @click.stop="alternarTitulos(row.item, row.toggleDetails)"
                >
                  <b-spinner v-if="row.item.carregandoTitulos" small />
                  <span v-else>{{ row.detailsShowing ? "Ocultar" : "Ver títulos" }}</span>
                </b-button>
              </template>
              <template #row-details="row">
                <div class="bg-light rounded p-4">
                  <b-alert v-if="row.item.erroTitulos" show variant="danger">
                    {{ row.item.erroTitulos }}
                  </b-alert>
                  <b-table
                    v-else
                    small
                    responsive
                    show-empty
                    empty-text="Nenhum título encontrado."
                    :items="row.item.titulos || []"
                    :fields="camposTitulos"
                  >
                    <template #cell(vendedora)="titulo">
                      {{ titulo.value || "-" }}
                    </template>
                    <template #cell(data_vencimento)="titulo">
                      {{ data(titulo.value) }}
                    </template>
                    <template #cell(dias_atraso)="titulo">
                      {{ inteiro(titulo.value) }} dias
                    </template>
                    <template #cell(saldo)="titulo">
                      <strong>{{ moeda(titulo.value) }}</strong>
                    </template>
                  </b-table>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import PessoaMixin from "@/modules/Application/mixins/PessoaMixin";

export default {
  name: "relatorio-inadimplencia",
  components: { VueSelect },
  mixins: [PessoaMixin],
  data() {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, "0");
    const dia = String(hoje.getDate()).padStart(2, "0");

    return {
      loading: false,
      exportando: null,
      erro: null,
      dados: null,
      inadimplentes: [],
      pessoas: [],
      pessoaSelecionada: null,
      filtros: {
        tipo: "TODOS",
        data_inicial: `${ano}-${mes}-01`,
        data_final: `${ano}-${mes}-${dia}`,
        faixa_atraso: "TODOS",
        id_pessoa: null,
        valor_minimo: null,
      },
      tipos: [
        { value: "TODOS", text: "Todos" },
        { value: "CLIENTE_FINAL", text: "Cliente final" },
        { value: "PARCEIRO", text: "Parceiro" },
      ],
      faixas: [
        { value: "TODOS", text: "Todas" },
        { value: "1_7", text: "1 a 7 dias" },
        { value: "8_15", text: "8 a 15 dias" },
        { value: "16_30", text: "16 a 30 dias" },
        { value: "31_60", text: "31 a 60 dias" },
        { value: "61_90", text: "61 a 90 dias" },
        { value: "MAIS_90", text: "Acima de 90 dias" },
      ],
      camposAging: [
        { key: "descricao", label: "Faixa" },
        { key: "cliente_final", label: "Cliente final", class: "text-right" },
        { key: "parceiros", label: "Parceiros", class: "text-right" },
        { key: "total", label: "Total", class: "text-right" },
        { key: "quantidade_titulos", label: "Títulos", class: "text-right" },
      ],
      camposInadimplentes: [
        { key: "nome", label: "Nome" },
        { key: "tipo", label: "Tipo" },
        { key: "vendedores", label: "Vendedor" },
        { key: "vencimento_mais_antigo", label: "Vencimento mais antigo" },
        { key: "dias_maior_atraso", label: "Maior atraso", class: "text-right" },
        {
          key: "quantidade_titulos_vencidos",
          label: "Títulos",
          class: "text-right",
        },
        { key: "valor_vencido", label: "Valor vencido", class: "text-right" },
        {
          key: "percentual_sobre_total",
          label: "% do total",
          class: "text-right",
        },
        { key: "acoes", label: "", class: "text-right" },
      ],
      camposTitulos: [
        { key: "documento", label: "Título" },
        { key: "historico", label: "Histórico" },
        { key: "vendedora", label: "Vendedor" },
        { key: "data_vencimento", label: "Vencimento" },
        { key: "dias_atraso", label: "Atraso", class: "text-right" },
        { key: "saldo", label: "Saldo", class: "text-right" },
      ],
    };
  },
  computed: {
    ...mapGetters(["isNuts"]),
    temDados() {
      return this.numero(this.dados?.resumo?.valor_total) > 0;
    },
  },
  mounted() {
    if (!this.isNuts) {
      this.$router.replace({ name: "relatoriosFinanceiro" });
      return;
    }
    this.gerarRelatorio();
  },
  methods: {
    payload() {
      return Object.keys(this.filtros).reduce((payload, chave) => {
        const valor = this.filtros[chave];
        if (valor !== null && valor !== "") payload[chave] = valor;
        return payload;
      }, {});
    },
    filtrosValidos() {
      if (!!this.filtros.data_inicial !== !!this.filtros.data_final) {
        this.$toast.warning("Informe a data inicial e final.");
        return false;
      }
      return true;
    },
    async gerarRelatorio() {
      if (!this.filtrosValidos()) return;

      this.loading = true;
      this.erro = null;
      try {
        const response = await this.$http.post(
          "/relatorios/inadimplencia",
          this.payload()
        );
        this.dados = response.data;
        this.inadimplentes = (response.data.inadimplentes || []).map((item) => ({
          ...item,
          _showDetails: false,
          titulos: null,
          carregandoTitulos: false,
          erroTitulos: null,
        }));
      } catch (error) {
        this.erro = this.mensagemErro(error);
      } finally {
        this.loading = false;
      }
    },
    async abrirPdf() {
      if (!this.filtrosValidos()) return;

      const janela = window.open("", "_blank");
      this.exportando = "pdf";
      try {
        const response = await this.$http.post(
          "/relatorios/inadimplencia/pdf",
          this.payload(),
          { responseType: "blob" }
        );
        const arquivo = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(arquivo);
        if (janela) {
          janela.location.href = url;
        } else {
          const link = document.createElement("a");
          link.href = url;
          link.download = "relatorio-inadimplencia.pdf";
          link.click();
        }
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (error) {
        if (janela) janela.close();
        this.$toast.error(this.mensagemErro(error));
      } finally {
        this.exportando = null;
      }
    },
    async exportarXlsx() {
      if (!this.filtrosValidos()) return;

      this.exportando = "xlsx";
      try {
        const response = await this.$http.post(
          "/relatorios/inadimplencia/xlsx",
          this.payload(),
          { responseType: "blob" }
        );
        const arquivo = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(arquivo);
        const link = document.createElement("a");
        link.href = url;
        link.download = "relatorio-inadimplencia.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.error(this.mensagemErro(error));
      } finally {
        this.exportando = null;
      }
    },
    selecionarPessoa(pessoa) {
      this.filtros.id_pessoa = pessoa?.id_pessoa || null;
    },
    buscarPessoa(search, loading) {
      const termo = String(search || "").trim();
      if (termo.length < 3) {
        this.buscarPessoaRemota.cancel();
        this.pessoas = [];
        loading(false);
        return;
      }
      loading(true);
      this.buscarPessoaRemota(loading, termo, this);
    },
    buscarPessoaRemota: _.debounce(async (loading, termo, vm) => {
      try {
        vm.pessoas = await vm.getClientes(termo);
      } finally {
        loading(false);
      }
    }, 400),
    async alternarTitulos(item, toggleDetails) {
      if (item._showDetails || item.titulos) {
        toggleDetails();
        return;
      }

      this.$set(item, "carregandoTitulos", true);
      this.$set(item, "erroTitulos", null);
      try {
        const response = await this.$http.post(
          "/relatorios/inadimplencia/titulos",
          {
            ...this.payload(),
            tipo: item.tipo,
            id_pessoa: item.id,
          }
        );
        this.$set(item, "titulos", response.data.titulos || []);
        toggleDetails();
      } catch (error) {
        this.$set(item, "erroTitulos", this.mensagemErro(error));
        toggleDetails();
      } finally {
        this.$set(item, "carregandoTitulos", false);
      }
    },
    numero(valor) {
      const numero = Number(valor);
      return Number.isFinite(numero) ? numero : 0;
    },
    moeda(valor) {
      return this.numero(valor).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    percentual(valor) {
      return `${this.numero(valor).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    },
    percentualSeguro(valor) {
      return Math.max(0, Math.min(100, this.numero(valor)));
    },
    inteiro(valor) {
      return Math.trunc(this.numero(valor)).toLocaleString("pt-BR");
    },
    data(valor) {
      if (!valor) return "-";
      const [ano, mes, dia] = String(valor).slice(0, 10).split("-");
      return ano && mes && dia ? `${dia}/${mes}/${ano}` : "-";
    },
    rotuloTipo(tipo) {
      return tipo === "CLIENTE_FINAL" ? "Cliente final" : "Parceiro";
    },
    mensagemErro(error) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        "Não foi possível gerar o relatório. Tente novamente."
      );
    },
  },
};
</script>

<style scoped>
.line-height-lg {
  line-height: 1.75;
}

@media (max-width: 767px) {
  .display-4 {
    font-size: 2rem;
  }
}
</style>
